import {
  ApiScheduledNotification as ApiScheduledNotification,
  PaginatedData,
} from "@api/interfaces";
import {
  Button,
  Chip,
  Search,
  Text,
  TextLink,
  VSpacer,
} from '@/components/DesignSystem';
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { InfiniteScroll } from '@/components/shared/InfiniteScroll';
import { Routes } from '@/constants/Routes';
import { formatDate, formatTime } from '@shared/utilities';
import i18next from 'i18next';
import { QueryKeys } from '../../../../constants/QueryKeys';
import { NotificationDetailModal } from '../modals/NotificationDetailModal';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { ScheduledNotificationApi } from "@/utilities/api/ScheduledNotificationApi";
import CancelScheduleSendIcon from '@mui/icons-material/CancelScheduleSend';
import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import React, { useState } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import {
  ScheduledNotificationStatus,
} from '@shared/enums/ScheduledNotificationStatus';
import {
  DeepLinkUrls,
  ScheduledNotificationCommunicationType,
} from "@shared/enums/ScheduledNotificationType";
import { FilterOptions } from '@shared/interfaces/ScheduledNotification';
import { ScheduledNotificationEndpoint } from '@api/endpoints/ScheduledNotificationEndpoint';
import { useAuthentication } from '@/contexts/dataSync/AuthenticationContext';
import { useTranslation } from 'react-i18next';
import { useNotificationStatusLabel } from '../utils/useNotificationStatusLabel';

export const NotificationBuilderTable: React.FC = () => {
  const { t } = useTranslation();
  const statusLabel = useNotificationStatusLabel();
  const { isInternal } = useAuthentication();
  const navigate = useNavigate();
  const scheduledNotificationRoute = isInternal
    ? Routes.ADMIN_SCHEDULED_NOTIFICATION
    : Routes.SCHEDULED_NOTIFICATION;
  const [sortBy, setSortBy] =
    useState<ScheduledNotificationEndpoint.List.ScheduledNotificationSort>();
  const [sortDesc, setSortDesc] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const { openSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  type UINotification = ApiScheduledNotification & {
    retailerName: string;
  };
  const [notificationToCancel, setNotificationToCancel] =
    useState<ApiScheduledNotification | null>(null);
  const [clickOnCancel, setClickOnCancel] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<UINotification | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const {
    data = { pages: [] as PaginatedData<ApiScheduledNotification>[] },
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery(
    [QueryKeys.GET_SCHEDULED_NOTIFICATIONS, searchInput, sortBy, sortDesc],
    async ({ pageParam = 0 }) =>
      ScheduledNotificationApi.list({
        page: pageParam,
        limit: 20,
        search: searchInput || undefined,
        sort: sortBy,
        sortDesc,
      }),
    {
      getNextPageParam: (lastPage) =>
        lastPage.page < lastPage.lastPage ? lastPage.page + 1 : undefined,
      refetchOnWindowFocus: true,
      refetchInterval: 5 * 60 * 1000,
      staleTime: 60 * 1000,
    },
  );

  const { mutateAsync: cancelNotification } = useMutation(
    async (notification: ApiScheduledNotification) => {
      return ScheduledNotificationApi.update(notification.id, {
        title: notification.title,
        communicationType: ScheduledNotificationCommunicationType.PointsExpiration,
        status: ScheduledNotificationStatus.Cancelled,
      });
    },
    {
      onSuccess: () => {
        void queryClient.invalidateQueries(QueryKeys.GET_SCHEDULED_NOTIFICATIONS);
        openSnackbar(t($ => $.notificationBuilder.table.success.cancelled));
      },
      onError: () => {
        openSnackbar(t($ => $.notificationBuilder.table.errors.cancelFailed));
      },
    },
  );

  const handleSort = (column: typeof sortBy) => {
    const isSortCurrentlySelected = column === sortBy;
    if (isSortCurrentlySelected) {
      if (column === 'createdAt') {
        setSortDesc(!sortDesc);
      } else {
        if (!sortDesc) {
          setSortBy(undefined);
          setSortDesc(true);
        } else {
          setSortDesc(false);
        }
      }
    } else {
      setSortBy(column);
      setSortDesc(true);
    }

    void queryClient.resetQueries([QueryKeys.GET_SCHEDULED_NOTIFICATIONS]);
  };

  const notificationCount = data?.pages?.[0]?.total ?? 0;

  const renderTableBody = () => {

    if (!data || data.pages.every(page => page.data.length === 0)) {
      return (
        <TableRow>
          <TableCell align="center" colSpan={6}>
            {searchInput ? (
              <Text>
                {t($ => $.notificationBuilder.table.emptyState.noResults, { searchInput })}
              </Text>
            ) : (
              <Text>
                {t($ => $.notificationBuilder.table.emptyState.noNotifications)}
              </Text>
            )}
          </TableCell>
        </TableRow>
      );
    }

    // Render directly from the pages data without creating an intermediate array
    return data.pages.flatMap(page => page.data.map(n => {
      const notification = {
        ...n,
        retailerName: n.retailer?.name ?? '—',
      };

      const recipientCount = notification.recipientCount ?? 0;

      return (
        <TableRow key={notification.id}>
          <TableCell>
            <Chip
              color={
                notification.status === ScheduledNotificationStatus.Scheduled
                  ? 'warning'
                  : notification.status === ScheduledNotificationStatus.Sent
                    ? 'success'
                    : undefined
              }
              label={
                <Text category="label-small" style={{ textTransform: 'uppercase' }}>
                  {statusLabel[notification.status]}
                </Text>
              }
              testID={`status-chip-${notification.id}`}
              variant="filled"
            />
          </TableCell>
          <TableCell>
            {notification.sendDate
              ? (
                `${
                  formatDate(notification.sendDate, i18next)
                }, ${
                  formatTime(notification.sendDate, i18next)
                }`
              )
              : '—'}
          </TableCell>
          <TableCell>
            <TextLink
              onClick={() => {
                if (recipientCount > 0) {
                  navigate(
                    scheduledNotificationRoute.replace(':id', notification.id),
                  );
                }
              }}
              testID="recipients-link"
            >
              {t(
                $ => $.notificationBuilder.table.recipientCount,
                { count: recipientCount },
              )}
            </TextLink>
          </TableCell>
          {isInternal && (
            <TableCell>{notification.retailerName}</TableCell>
          )}
          <TableCell>
            <TextLink
              onClick={() => {
                setSelectedNotification(notification);
                setShowDetailModal(true);
              }}
              testID="title-link"
            >
              {notification.title === null ? '-' : notification.title}
            </TextLink>

          </TableCell>
          <TableCell>
            {notification.status === 'Scheduled' && (
              <Button color="error"
                onClick={() => {
                  setNotificationToCancel(n);
                  setClickOnCancel(true);
                }}
                size="small"
                square
                startIcon={<CancelScheduleSendIcon />}
                testID="cancel-button"
              >
                {t($ => $.common.actions.cancelSend)}
              </Button>
            )}
          </TableCell>
        </TableRow>
      );
    }));
  };

  return (
    <>
      <Stack alignItems="center" direction="row" spacing={6}>
        <>
          <Search
            fullWidth={false}
            onChangeText={setSearchInput}
            testID="notification-search"
            value={searchInput}
            width={440}
          />
          <VSpacer size="6" />
        </>
      </Stack>
      <VSpacer size="6" />
      <Stack alignItems="center" direction="row" spacing={6}>
        <Text category="body-medium">
          {t(
            $ => $.notificationBuilder.table.customNotificationCount,
            { count: notificationCount },
          )}
        </Text>
      </Stack>
      <VSpacer size="6" />
      <InfiniteScroll
        fetchMoreItems={fetchNextPage}
        hasMore={hasNextPage ?? true}
        height="42vh"
        isLoading={isFetchingNextPage}
      >
        <Table sx={{ tableLayout: 'fixed' }}>
          <TableHead
            sx={{ backgroundColor: 'white', position: 'sticky', top: 0, zIndex: 10 }}>
            <TableRow>
              <TableCell width={180}>
                <TableSortLabel
                  active={sortBy === 'status'}
                  direction={sortDesc ? 'desc' : 'asc'}
                  onClick={() => handleSort('status')}
                >
                  {t($ => $.notificationBuilder.table.headers.status)}
                </TableSortLabel>
              </TableCell>
              <TableCell width={200}>
                <TableSortLabel
                  active={sortBy === 'sendDate'}
                  direction={sortDesc ? 'desc' : 'asc'}
                  onClick={() => handleSort('sendDate')}
                >
                  {t($ => $.notificationBuilder.table.headers.sendDate)}
                </TableSortLabel>
              </TableCell>
              <TableCell width={160}>
                {t($ => $.notificationBuilder.table.headers.recipients)}
              </TableCell>
              {isInternal && (
                <TableCell width={180}>
                  <TableSortLabel
                    active={sortBy === 'retailerName'}
                    direction={sortDesc ? 'desc' : 'asc'}
                    onClick={() => handleSort('retailerName')}
                  >
                    {t($ => $.notificationBuilder.table.headers.retailer)}
                  </TableSortLabel>
                </TableCell>
              )}
              <TableCell width={280}>
                <TableSortLabel
                  active={sortBy === 'title'}
                  direction={sortDesc ? 'desc' : 'asc'}
                  onClick={() => handleSort('title')}
                >
                  {t($ => $.notificationBuilder.table.headers.title)}
                </TableSortLabel>
              </TableCell>
              <TableCell width={160}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{renderTableBody()}</TableBody>
        </Table>
      </InfiniteScroll>
      {clickOnCancel && notificationToCancel && (
        <ConfirmDialog
          isLoading={isLoading}
          onCancel={() => {
            setClickOnCancel(false);
            setNotificationToCancel(null);
          }}
          onConfirm={async () => {
            await cancelNotification(notificationToCancel);
            setClickOnCancel(false);
            setNotificationToCancel(null);
          }}
          open={clickOnCancel}
          testID="confirm-dialog"
          title={t($ => $.notificationBuilder.table.confirm.cancel)}
        />
      )}

      {selectedNotification && showDetailModal && (
        <NotificationDetailModal
          communicationType={selectedNotification.communicationType}
          csvRecipientData={selectedNotification.csvRecipientData || []}
          deepLink={(selectedNotification.deepLink as DeepLinkUrls) || null}
          message={selectedNotification.message || ''}
          mode="edit"
          notificationId={selectedNotification.id}
          onClose={() => {
            setSelectedNotification(null);
            setShowDetailModal(false);
            void queryClient.invalidateQueries([QueryKeys.GET_SCHEDULED_NOTIFICATIONS]);
          }}
          open={showDetailModal}
          options={(selectedNotification.options || {}) as FilterOptions}
          recipientCount={selectedNotification.recipientCount}
          retailerId={selectedNotification.retailerId || ''}
          retailerName={selectedNotification.retailerName || ''}
          sendDate={
            selectedNotification.sendDate
              ? new Date(selectedNotification.sendDate)
              : undefined
          }
          status={selectedNotification.status}
          testID="notification-detail-modal"
          title={selectedNotification.title || ''}
        />
      )}
    </>
  );
};
